import React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiFileText,
  FiCheckCircle,
  FiClock,
  FiAlertTriangle,
  FiXCircle,
  FiSend,
  FiRefreshCw,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { taxService } from '../../services/apiService';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/formatters';

const TaxFilingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: filingData,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery(['tax-filing', id], () => taxService.getFiling(id), {
    refetchOnWindowFocus: false,
  });

  const submitMutation = useMutation(taxService.submitFiling, {
    onSuccess: () => {
      toast.success('Filing submitted to LRA');
      queryClient.invalidateQueries(['tax-filing', id]);
      queryClient.invalidateQueries('tax-filings');
      queryClient.invalidateQueries('tax-summary');
    },
    onError: (err) => {
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Failed to submit filing';
      toast.error(msg);
    },
  });

  if (isLoading) return <LoadingSpinner message="Loading filing..." />;

  if (error) {
    return (
      <div>
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              className="icon-btn"
              onClick={() => navigate('/tax/filing')}
            >
              <FiArrowLeft size={20} />
            </button>
            <h1 className="page-title">Filing Not Found</h1>
          </div>
        </div>
        <div className="card">
          <div className="empty-state">
            <FiAlertTriangle size={48} color="#ef4444" />
            <h3>Unable to load filing</h3>
            <p>{error.message}</p>
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/tax/filing')}
            >
              Back to Filings
            </button>
          </div>
        </div>
      </div>
    );
  }

  const filing = filingData?.data || {};
  const isDraft = filing.status === 'draft';
  const isSubmitted = filing.status === 'submitted';
  const isApproved = filing.status === 'approved';
  const isRejected = filing.status === 'rejected';

  const handleSubmitFiling = () => {
    if (
      !window.confirm(
        'Submit this filing to the LRA? Once submitted, it cannot be edited.'
      )
    ) {
      return;
    }
    submitMutation.mutate(filing.filing_id);
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            className="icon-btn"
            onClick={() => navigate('/tax/filing')}
          >
            <FiArrowLeft size={20} />
          </button>
          <div>
            <h1 className="page-title">Filing Details</h1>
            <p className="page-subtitle" style={{ fontFamily: 'monospace', fontSize: '13px' }}>
              {filing.filing_id}
            </p>
          </div>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-secondary"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <FiRefreshCw size={18} />
            Refresh
          </button>
          {isDraft && (
            <button
              className="btn btn-primary"
              onClick={handleSubmitFiling}
              disabled={submitMutation.isLoading}
            >
              <FiSend size={18} />
              {submitMutation.isLoading ? 'Submitting...' : 'Submit to LRA'}
            </button>
          )}
        </div>
      </div>

      {/* Status banner */}
      <StatusBanner status={filing.status} />

      {/* Summary cards */}
      <div className="stats-grid" style={{ marginTop: '20px' }}>
        <div className="stat-card">
          <div className="stat-info">
            <div className="stat-value">{formatCurrency(filing.gross_sales || 0)}</div>
            <div className="stat-label">Gross Sales</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <div className="stat-value">{formatCurrency(filing.net_sales || 0)}</div>
            <div className="stat-label">Net Sales</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <div className="stat-value" style={{ color: '#1a73e8' }}>
              {formatCurrency(filing.tax_due || 0)}
            </div>
            <div className="stat-label">Tax Due</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <div className="stat-value">{filing.total_transactions || 0}</div>
            <div className="stat-label">Transactions</div>
          </div>
        </div>
      </div>

      {/* Details grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: '24px',
          marginTop: '24px',
        }}
      >
        {/* Filing Information */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Filing Information</h2>
          </div>
          <DetailRow label="Filing ID" value={filing.filing_id} mono />
          <DetailRow
            label="Cycle"
            value={
              <span style={{ textTransform: 'capitalize' }}>
                {filing.cycle}
              </span>
            }
          />
          <DetailRow
            label="Period"
            value={`${formatDate(filing.period_start)} — ${formatDate(
              filing.period_end
            )}`}
          />
          <DetailRow
            label="Status"
            value={<StatusBadge status={filing.status} />}
          />
          <DetailRow
            label="Created"
            value={formatDateTime(filing.created_at)}
          />
          {filing.submitted_at && (
            <DetailRow
              label="Submitted"
              value={formatDateTime(filing.submitted_at)}
            />
          )}
          {filing.approved_at && (
            <DetailRow
              label="Approved"
              value={formatDateTime(filing.approved_at)}
            />
          )}
          {filing.rejected_at && (
            <DetailRow
              label="Rejected"
              value={formatDateTime(filing.rejected_at)}
            />
          )}
        </div>

        {/* Tax Breakdown */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Tax Breakdown</h2>
          </div>
          <DetailRow
            label="Gross Sales"
            value={formatCurrency(filing.gross_sales || 0)}
          />
          <DetailRow
            label="Discounts"
            value={formatCurrency(filing.total_discount || 0)}
          />
          <DetailRow
            label="Taxable Sales"
            value={formatCurrency(filing.taxable_sales || 0)}
            bold
          />
          <div
            style={{
              borderTop: '1px solid #e5e7eb',
              margin: '12px 0',
            }}
          />
          <DetailRow
            label="Tax Collected"
            value={formatCurrency(filing.tax_collected || 0)}
          />
          <DetailRow
            label="Tax Due"
            value={formatCurrency(filing.tax_due || 0)}
            bold
            accent="#1a73e8"
            large
          />
        </div>
      </div>

      {/* Notes */}
      {filing.notes && (
        <div className="card" style={{ marginTop: '24px' }}>
          <div className="card-header">
            <h2 className="card-title">Notes</h2>
          </div>
          <p
            style={{
              fontSize: '14px',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              color: '#374151',
            }}
          >
            {filing.notes}
          </p>
        </div>
      )}

      {/* Rejection reason */}
      {isRejected && filing.rejection_reason && (
        <div
          className="card"
          style={{
            marginTop: '24px',
            borderLeft: '4px solid #ef4444',
            background: '#fef2f2',
          }}
        >
          <div className="card-header">
            <h2 className="card-title" style={{ color: '#991b1b' }}>
              Rejection Reason
            </h2>
          </div>
          <p style={{ color: '#991b1b', fontSize: '14px' }}>
            {filing.rejection_reason}
          </p>
        </div>
      )}

      {/* Submitted/Approved info panel */}
      {isSubmitted && (
        <div
          className="card"
          style={{
            marginTop: '24px',
            background: '#f0f9ff',
            border: '1px solid #0ea5e9',
          }}
        >
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            <FiClock size={24} color="#0284c7" />
            <div>
              <div style={{ fontWeight: 600, color: '#0c4a6e' }}>
                Awaiting LRA Review
              </div>
              <div
                style={{
                  fontSize: '13px',
                  color: '#0369a1',
                  marginTop: '4px',
                }}
              >
                Your filing has been submitted. The LRA will review it and
                notify you once a decision is made.
              </div>
            </div>
          </div>
        </div>
      )}

      {isApproved && (
        <div
          className="card"
          style={{
            marginTop: '24px',
            background: '#f0fdf4',
            border: '1px solid #22c55e',
          }}
        >
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            <FiCheckCircle size={24} color="#16a34a" />
            <div>
              <div style={{ fontWeight: 600, color: '#14532d' }}>
                Filing Approved
              </div>
              <div
                style={{
                  fontSize: '13px',
                  color: '#166534',
                  marginTop: '4px',
                }}
              >
                This filing has been approved by the LRA and is now part of
                your compliance record.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Helper components ----------

const DetailRow = ({ label, value, bold, large, accent, mono }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 0',
      borderBottom: '1px solid #f3f4f6',
      fontSize: large ? '16px' : '14px',
    }}
  >
    <span style={{ color: '#6b7280' }}>{label}</span>
    <span
      style={{
        fontWeight: bold ? 700 : 500,
        color: accent || '#1f2937',
        fontFamily: mono ? 'monospace' : 'inherit',
        fontSize: mono ? '12px' : 'inherit',
      }}
    >
      {value}
    </span>
  </div>
);

const StatusBadge = ({ status }) => {
  const colors = {
    draft: { bg: '#f3f4f6', color: '#6b7280' },
    submitted: { bg: '#dbeafe', color: '#1e40af' },
    approved: { bg: '#dcfce7', color: '#166534' },
    rejected: { bg: '#fee2e2', color: '#991b1b' },
    amended: { bg: '#fef3c7', color: '#92400e' },
  };
  const c = colors[status] || colors.draft;
  return (
    <span
      style={{
        background: c.bg,
        color: c.color,
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'capitalize',
      }}
    >
      {status}
    </span>
  );
};

const StatusBanner = ({ status }) => {
  const config = {
    draft: {
      bg: '#f9fafb',
      border: '#d1d5db',
      icon: <FiFileText size={20} color="#6b7280" />,
      title: 'Draft — Not Yet Submitted',
      desc: 'This filing is a draft. Review the numbers and submit when ready.',
      titleColor: '#374151',
    },
    submitted: {
      bg: '#eff6ff',
      border: '#3b82f6',
      icon: <FiClock size={20} color="#1d4ed8" />,
      title: 'Submitted — Under LRA Review',
      desc: 'The LRA has received your filing and will review it.',
      titleColor: '#1e40af',
    },
    approved: {
      bg: '#f0fdf4',
      border: '#22c55e',
      icon: <FiCheckCircle size={20} color="#16a34a" />,
      title: 'Approved',
      desc: 'This filing has been approved by the LRA.',
      titleColor: '#166534',
    },
    rejected: {
      bg: '#fef2f2',
      border: '#ef4444',
      icon: <FiXCircle size={20} color="#dc2626" />,
      title: 'Rejected',
      desc: 'The LRA has rejected this filing. See the reason below.',
      titleColor: '#991b1b',
    },
  };
  const c = config[status] || config.draft;
  return (
    <div
      style={{
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: '8px',
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}
    >
      {c.icon}
      <div>
        <div style={{ fontWeight: 600, color: c.titleColor }}>{c.title}</div>
        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: 2 }}>
          {c.desc}
        </div>
      </div>
    </div>
  );
};

export default TaxFilingDetail;
